<template>
  <div
    :class="[
      type === 'textarea' ? 'zm-textarea' : 'zm-input',
      inputSize ? 'zm-input--' + inputSize : '',
      {
        'is-disabled': inputDisabled,
        'is-exceed': inputExceed,
        'is-empty': inputEmpty,
        'is-focused': inlineFocus,
        'zm-input-group': $slots.prepend || $slots.append,
        'zm-input-group--append': $slots.append,
        'zm-input-group--prepend': $slots.prepend,
        'zm-input--prefix': $slots.prefix || prefixIcon,
        'zm-input--suffix': $slots.suffix || suffixIcon || clearable || showPassword,
        'zm-input--suffix-large': ($slots.suffix || suffixIcon) && ( clearable || showPassword) || ( clearable && showPassword),
        'zm-input--show-label': showInlineLabel
      }
    ]"
    @mouseenter="hovering = true"
    @mouseleave="hovering = false"
  >
    <template v-if="type !== 'textarea'">
      <!-- 前置元素 -->
      <div class="zm-input-group__prepend" v-if="$slots.prepend">
        <slot name="prepend"></slot>
      </div>

      <input
        v-if="type !== 'textarea'"
        class="zm-input__inner"
        v-bind="$attrs"
        :type="showPassword ? (passwordVisible ? 'text': 'password') : type"
        :disabled="inputDisabled"
        :readonly="readonly"
        :autocomplete="autocompleteFill"
        :name="name"
        :placeholder="placeholder"
        ref="input"
        @compositionstart="handleCompositionStart"
        @compositionend="handleCompositionEnd"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @change="handleChange"
        @keyup.enter="handleSubmit"
        :aria-required="isRequired"
        :aria-disabled="inputDisabled"
        :aria-label="label || inlineLabelText"
        :aria-labelledby="labelBy"
        :aria-invalid="isShowErrorMessage"
      >
      <label v-if="showInlineLabel" class="zm-input__label" :for="labelFor">{{ inlineLabelText }}</label>
      <!-- 前置内容 -->
      <span class="zm-input__prefix" v-if="$slots.prefix || prefixIcon">
        <slot name="prefix"></slot>
        <i
          class="zm-input__icon"
          v-if="prefixIcon"
          :class="prefixIcon"
        >
        </i>
      </span>
      <!-- 后置内容 -->
      <span
        class="zm-input__suffix"
        v-show="$slots.suffix || suffixIcon || showClear|| showPassword || validateState && needStatusIcon"
      > <!-- 使用 v-show 代替 v-if 防止 input re-render 丢失焦点-->
        <span class="zm-input__suffix-inner">
          <template v-if="!showClear || !showPwdVisible">
            <slot name="suffix"></slot>
            <i
              class="zm-input__icon"
              v-if="suffixIcon"
              :class="suffixIcon"
            />
          </template>
          <button
            v-if="showPassword"
            type="button"
            :aria-label="passwordVisible ? 'hide password' : 'show password'"
            :class="['zm-input__password-btn', 'zm-input__icon', passwordVisible ? 'zm-icon-eyes-close' : 'zm-icon-eyes', 'zm-input__clear']"
            @click="handlePasswordVisible"
          />
          <button
            v-if="showClear"
            type="button"
            aria-label="Clear"
            class="zm-input__icon zm-icon-error zm-input__clear"
            @click="clear"
          />
          <span v-if="isWordLimitVisible" class="zm-input__count">
            <span class="zm-input__count-inner">
              {{ textLength }}/{{ upperLimit }}
            </span>
          </span>
        </span>
        <i
          class="zm-input__icon"
          v-if="validateState"
          :class="['zm-input__validateIcon', validateIcon]"
        />
      </span>
      <!-- 后置元素 -->
      <div class="zm-input-group__append" v-if="$slots.append">
        <slot name="append"></slot>
      </div>
    </template>
    <textarea
      v-else
      class="zm-textarea__inner"
      @compositionstart="handleCompositionStart"
      @compositionend="handleCompositionEnd"
      @input="handleInput"
      ref="textarea"
      v-bind="$attrs"
      :disabled="inputDisabled"
      :readonly="readonly"
      :autocomplete="autocomplete"
      :name="name"
      :style="textareaStyle"
      @focus="handleFocus"
      @blur="handleBlur"
      @change="handleChange"
      @keyup.enter="handleSubmit"
      :aria-required="isRequired"
      :aria-disabled="inputDisabled"
      :aria-label="label"
      :aria-labelledby="labelBy"
      :aria-invalid="isShowErrorMessage"
      :aria-describedby="isWordLimitVisible && type === 'textarea' ? wordLimitId: null"
    >
    </textarea>
    <template v-if="isWordLimitVisible && type === 'textarea'">
      <span class="zm-input__count">
        {{ textLength }}/{{ upperLimit }}
      </span>
      <span :id="wordLimitId" class="zm-sr-only">You can only use {{ upperLimit }} characters in this field.</span>
      <span class="zm-sr-only" role="status">You have used {{ textLength }} characters in this field.</span>
    </template>
  </div>
</template>
<script>
  import emitter from '../../mixins/emitter';
  import calcTextareaHeight from './calcTextareaHeight';
  import merge from '../../utils/merge';
  import { generateId } from '../../utils/util';
  import { isChrome } from '../../utils/env';
  let _uid = 0;
  export default {
    name: 'ZmInput',

    componentName: 'ZmInput',

    mixins: [emitter],

    inheritAttrs: false,

    inject: {
      ZmForm: {
        default: ''
      },
      ZmFormItem: {
        default: ''
      }
    },

    data() {
      return {
        textareaCalcStyle: {},
        hovering: false,
        focused: false,
        isComposing: false,
        passwordVisible: false,
        name: ''
      };
    },

    props: {
      value: [String, Number],
      size: {
        type: String,
        default: 'small'
      },
      resize: String,
      disabled: Boolean,
      readonly: Boolean,
      type: {
        type: String,
        default: 'text'
      },
      autosize: {
        type: [Boolean, Object],
        default: false
      },
      autocomplete: {
        type: String,
        default: 'off'
      },
      validateEvent: {
        type: Boolean,
        default: true
      },
      inlineLabel: String,
      suffixIcon: String,
      prefixIcon: String,
      ariaRequired: Boolean,
      label: String,
      labelBy: String,
      clearable: Boolean,
      alwaysShowClear: Boolean,
      showPassword: Boolean,
      showWordLimit: Boolean
    },

    computed: {
      _elFormItemSize() {
        return (this.ZmFormItem || {}).elFormItemSize;
      },
      validateState() {
        return this.ZmFormItem ? this.ZmFormItem.validateState : '';
      },
      isShowErrorMessage() {
        return this.ZmFormItem && this.ZmFormItem.isShowErrorMessage || false;
      },
      needStatusIcon() {
        return this.ZmForm ? this.ZmForm.statusIcon : false;
      },
      validateIcon() {
        return {
          validating: 'zm-icon-loading',
          success: 'zm-icon-success',
          error: 'zm-icon-error'
        }[this.validateState];
      },
      textareaStyle() {
        return merge({}, this.textareaCalcStyle, { resize: this.resize });
      },
      labelFor() {
        return (this.ZmForm && this.ZmForm.labelPosition === 'inline' && this.ZmFormItem && this.ZmFormItem.labelFor) || this.$attrs.id;
      },
      emptyText() {
        return String(this.value).trim().length === 0;
      },
      showInlineLabel() {
        return (this.ZmForm && this.ZmForm.labelPosition === 'inline') || this.inlineLabel;
      },
      inlineFocus() {
        if (!this.showInlineLabel) {
          return false;
        }
        return this.focused && this.emptyText || !this.emptyText;
      },
      placeholder() {
        return this.showInlineLabel ? '' : this.$attrs.placeholder;
      },
      inlineLabelText() {
        if ((this.ZmForm && this.ZmForm.labelPosition !== 'inline') && !this.inlineLabel) {
          return '';
        }
        if (this.ZmFormItem && this.ZmFormItem.label) {
          return this.ZmFormItem.label;
        } else {
          return this.inlineLabel;
        }
      },
      inputSize() {
        if (this.ZmForm && this.ZmForm.labelPosition === 'inline' || this.inlineLabel) {
          return 'xLarge';
        } else {
          return this._elFormItemSize || this.size || (this.$ELEMENT || {}).size;
        }
      },
      inputEmpty() {
        if (typeof this.value === 'undefined') {
          return true;
        }
        return String(this.value).length === 0;
      },
      inputDisabled() {
        return this.disabled || (this.ZmForm || {}).disabled;
      },
      nativeInputValue() {
        return this.value === null || this.value === undefined ? '' : String(this.value);
      },
      showClear() {
        return this.clearable &&
          !this.inputDisabled &&
          !this.readonly &&
          this.nativeInputValue &&
          (this.focused || this.hovering || this.alwaysShowClear);
      },
      showPwdVisible() {
        return this.showPassword &&
          !this.inputDisabled &&
          !this.readonly;
      },
      autocompleteFill() {
        if (this.autocomplete === 'off' && isChrome) {
          return this.name;
        } else {
          return this.autocomplete;
        }
      },
      isRequired() {
        return this.ZmFormItem && this.ZmFormItem.isRequired || this.ariaRequired;
      },
      isWordLimitVisible() {
        return this.showWordLimit &&
          this.$attrs.maxlength &&
          (this.type === 'text' || this.type === 'textarea') &&
          !this.inputDisabled &&
          !this.readonly &&
          !this.showPassword;
      },
      upperLimit() {
        return this.$attrs.maxlength;
      },
      textLength() {
        if (typeof this.value === 'number') {
          return String(this.value).length;
        }

        return (this.value || '').length;
      },
      inputExceed() {
        // show exceed style if length of initial value greater then maxlength
        return this.isWordLimitVisible &&
          (this.textLength > this.upperLimit);
      },
      wordLimitId() {
        return `word-limit-${this.id || _uid++}`;
      }
    },

    watch: {
      value(val) {
        this.$nextTick(this.resizeTextarea);
        if (this.validateEvent) {
          this.dispatch('ZmFormItem', 'el.form.change', [val]);
        }
      },
      // native input value is set explicitly
      // do not use v-model / :value in template
      // see: https://github.com/ElemeFE/element/issues/14521
      nativeInputValue() {
        this.setNativeInputValue();
      },
      // when change between <input> and <textarea>,
      // update DOM dependent value and styles
      // https://github.com/ElemeFE/element/issues/14857
      type() {
        this.$nextTick(() => {
          this.setNativeInputValue();
          this.resizeTextarea();
          this.updateIconOffset();
        });
      }
    },

    methods: {
      focus() {
        this.getInput().focus();
      },
      blur() {
        this.getInput().blur();
      },
      handleBlur(event) {
        this.focused = false;
        if (isChrome) {
          if (this.$attrs && this.$attrs.name) {
            this.name = this.$attrs.name;
          } else {
            this.name = '';
          }
        }
        this.$emit('blur', event);
        if (this.validateEvent) {
          this.dispatch('ZmFormItem', 'el.form.blur', [this.value]);
        }
      },
      select() {
        this.getInput().select();
      },
      resizeTextarea() {
        if (this.$isServer) return;
        const { autosize, type } = this;
        if (type !== 'textarea') return;
        if (!autosize) {
          this.textareaCalcStyle = {
            minHeight: calcTextareaHeight(this.$refs.textarea).minHeight
          };
          return;
        }
        const minRows = autosize.minRows;
        const maxRows = autosize.maxRows;

        this.textareaCalcStyle = calcTextareaHeight(this.$refs.textarea, minRows, maxRows);
      },
      setNativeInputValue() {
        const input = this.getInput();
        if (!input) return;
        if (input.value === this.nativeInputValue) return;
        input.value = this.nativeInputValue;
      },
      handleFocus(event) {
        this.focused = true;
        if (isChrome) {
          if (this.$attrs && this.$attrs.name) {
            this.name = `new-${this.$attrs.name}`;
          } else {
            this.name = `input-name-${generateId()}`;
          }
        }
        this.$emit('focus', event);
      },
      handleCompositionStart() {
        this.isComposing = true;
      },
      handleCompositionEnd(event) {
        this.isComposing = false;
        this.handleInput(event);
      },
      handleInput(event) {
        // should not emit input during composition
        // see: https://github.com/ElemeFE/element/issues/10516
        if (this.isComposing) return;

        // hack for https://github.com/ElemeFE/element/issues/8548
        // should remove the following line when we don't support IE
        if (event.target.value === this.nativeInputValue) return;

        this.$emit('input', event.target.value);

        // ensure native input value is controlled
        // see: https://github.com/ElemeFE/element/issues/12850
        this.$nextTick(this.setNativeInputValue);
      },
      handleChange(event) {
        this.$emit('change', event.target.value);
      },
      handleSubmit(event) {
        this.$emit('submit', event.target.value);
      },
      calcIconOffset(place) {
        let elList = [].slice.call(this.$el.querySelectorAll(`.zm-input__${place}`) || []);
        if (!elList.length) return;
        let el = null;
        for (let i = 0; i < elList.length; i++) {
          if (elList[i].parentNode === this.$el) {
            el = elList[i];
            break;
          }
        }
        if (!el) return;
        const pendantMap = {
          suffix: 'append',
          prefix: 'prepend'
        };

        const pendant = pendantMap[place];
        if (this.$slots[pendant]) {
          el.style.transform = `translateX(${place === 'suffix' ? '-' : ''}${this.$el.querySelector(`.zm-input-group__${pendant}`).offsetWidth}px)`;
        } else {
          el.removeAttribute('style');
        }
      },
      updateIconOffset() {
        this.calcIconOffset('prefix');
        this.calcIconOffset('suffix');
      },
      clear() {
        this.$emit('input', '');
        this.$emit('change', '');
        this.$emit('clear');
      },
      handlePasswordVisible() {
        this.passwordVisible = !this.passwordVisible;
        if (this.passwordVisible && !this.value.length) {
          this.focus();
        }
      },
      getInput() {
        return this.$refs.input || this.$refs.textarea;
      }
    },

    created() {
      this.$on('inputSelect', this.select);
      this.name = this.$attrs && this.$attrs.name || '';
    },

    mounted() {
      this.setNativeInputValue();
      this.resizeTextarea();
      this.updateIconOffset();
    },

    updated() {
      this.$nextTick(this.updateIconOffset);
    }
  };
</script>

<style lang="scss">
  @import "./style.scss";
</style>
